import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import styled from "@emotion/styled";

/**
 * Tutoriel interactif : met en évidence (spotlight) l'élément visé par
 * chaque étape (sélecteur [data-tour="..."]) avec une bulle d'explication.
 * L'élément reste cliquable : l'utilisateur peut faire l'action pendant le tutoriel.
 */
const TutorialOverlay = (props) => {

  const { guide, stepIndex, onPrev, onNext, onClose } = props;

  const step = guide.steps[stepIndex];
  const isLast = stepIndex === guide.steps.length - 1;

  // Exécute l'action de l'étape (clic sur le bouton visé, focus dans le champ)
  const executeAction = () => {
    if (!step.action || !step.target) {
      return;
    }
    const element = document.querySelector(step.target);
    if (!element) {
      return;
    }
    if (step.actionWhen && !step.actionWhen(element)) {
      return;
    }
    if (step.action === "click") {
      element.click();
    } else if (step.action === "focus") {
      const input = element.querySelector("input") || element;
      input.focus();
    }
  };

  const handleNext = () => {
    executeAction();
    onNext();
  };

  const handleFinish = () => {
    executeAction();
    onClose();
  };

  const [rect, setRect] = useState(null);

  useEffect(() => {
    const element = step.target ? document.querySelector(step.target) : null;

    if (!element) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const bounds = element.getBoundingClientRect();
      setRect({ top: bounds.top, left: bounds.left, width: bounds.width, height: bounds.height });
    };

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(updateRect, 350);

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [step]);

  // Position de la bulle : sous l'élément si possible, sinon au-dessus ; centrée sans cible
  const tooltipStyle = {};
  if (rect) {
    const below = rect.top + rect.height + 190 < window.innerHeight;
    tooltipStyle.left = Math.max(12, Math.min(rect.left, window.innerWidth - 372));
    if (below) {
      tooltipStyle.top = rect.top + rect.height + 14;
    } else {
      tooltipStyle.top = Math.max(12, rect.top - 14);
      tooltipStyle.transform = "translateY(-100%)";
    }
  } else {
    tooltipStyle.top = "50%";
    tooltipStyle.left = "50%";
    tooltipStyle.transform = "translate(-50%, -50%)";
  }

  return (
    <>
      {rect ? (
        <Spotlight
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      ) : (
        <Dim />
      )}

      <Tooltip style={tooltipStyle}>
        <TooltipHeader>
          <strong>{guide.title}</strong>
          <CloseButton onClick={onClose} title="Quitter le tutoriel">✕</CloseButton>
        </TooltipHeader>
        <StepCounter>Étape {stepIndex + 1} / {guide.steps.length}</StepCounter>
        <p className="mb-2">{step.text}</p>
        {step.action && (
          <ActionHint>
            « {isLast ? "Terminer" : "Suivant"} » effectue cette action pour vous.
          </ActionHint>
        )}
        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={stepIndex === 0}
            onClick={onPrev}
          >
            ← Précédent
          </Button>
          <Button variant="primary" size="sm" onClick={isLast ? handleFinish : handleNext}>
            {isLast ? "Terminer" : "Suivant →"}
          </Button>
        </div>
      </Tooltip>
    </>
  );
};

// La grande ombre portée crée l'assombrissement autour de la zone mise en évidence
const Spotlight = styled.div`
  position: fixed;
  z-index: 3000;
  border: 2px solid #10b981;
  border-radius: 10px;
  box-shadow: 0 0 0 9999px rgba(17, 24, 39, 0.55);
  pointer-events: none;
  transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease;
`;

const Dim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(17, 24, 39, 0.55);
  pointer-events: none;
`;

const Tooltip = styled.div`
  position: fixed;
  z-index: 3001;
  width: 360px;
  max-width: calc(100vw - 24px);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const StepCounter = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  margin: 2px 0 6px;
`;

const ActionHint = styled.div`
  color: #059669;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  color: #6b7280;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: #111827;
  }
`;

TutorialOverlay.propTypes = {
  guide: PropTypes.shape({
    title: PropTypes.string,
    steps: PropTypes.array,
  }).isRequired,
  stepIndex: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TutorialOverlay;
